import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

/* ------------------------------------------------------------------
    table 
------------------------------------------------------------------ */

// | --- | :---: | ---: |
const DELIMITER_RE = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)*\|?\s*$/;

function isTableLine(line) {
    return line.includes('|');
}

// `\|` 셀 분리
function splitRow(line) {
    const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    const cells = [];
    let cur = '';

    for (let i = 0; i < trimmed.length; i += 1) {
        const ch = trimmed[i];
        if (ch === '\\' && trimmed[i + 1] === '|') {
            cur += '|';
            i += 1;
            continue;
        }
        if (ch === '|') {
            cells.push(cur.trim());
            cur = '';
            continue;
        }
        cur += ch;
    }
    cells.push(cur.trim());
    return cells;
}

function parseAlign(line) {
    return splitRow(line).map((cell) => {
        const left = cell.startsWith(':');
        const right = cell.endsWith(':');
        if (left && right) return 'center';
        if (right) return 'right';
        if (left) return 'left';
        return null;
    });
}

/**
 * 원문을 text / table 블록 배열로 분해
 * @param {string} src
 * @returns {Array<{type:'text',value:string}|{type:'table',head:string[],align:(string|null)[],rows:string[][]}>}
 */
export function splitTableBlocks(src) {
    if (typeof src !== 'string' || src.length === 0) return [];

    const lines = src.split('\n');
    const blocks = [];
    let buffer = [];

    const flushText = () => {
        if (buffer.length === 0) return;
        const value = buffer.join('\n');
        if (value.trim().length > 0) blocks.push({ type: 'text', value });
        buffer = [];
    };

    let i = 0;
    while (i < lines.length) {
        const header = lines[i];
        const delimiter = lines[i + 1];

        const isTableStart =
            isTableLine(header) &&
            typeof delimiter === 'string' &&
            DELIMITER_RE.test(delimiter) &&
            isTableLine(delimiter);

        if (!isTableStart) {
            buffer.push(header);
            i += 1;
            continue;
        }

        const head = splitRow(header);
        const align = parseAlign(delimiter);

        // GFM: 헤더 셀 수와 구분선 셀 수가 같아야 표로 인정
        if (head.length !== align.length) {
            buffer.push(header);
            i += 1;
            continue;
        }

        const rows = [];
        let j = i + 2;
        while (j < lines.length && isTableLine(lines[j]) && lines[j].trim() !== '') {
            const cells = splitRow(lines[j]);
            
            const normalized = Array.from({ length: head.length }, (_, k) => cells[k] ?? '');
            rows.push(normalized);
            j += 1;
        }

        flushText();
        blocks.push({ type: 'table', head, align, rows });
        i = j;
    }

    flushText();
    return blocks;
}

/* ------------------------------------------------------------------
   렌더러
------------------------------------------------------------------ */

function Unwrapped({ children }) {
    return <>{children}</>;
}
const CELL_COMPONENTS = { p: Unwrapped };

const BLOCK_COMPONENTS = {
    a({ href, children, ...rest }) {
        const external = typeof href === 'string' && /^https?:\/\//i.test(href);
        return (
            <a {...rest} href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {children}
            </a>
        );
    },
};

function Cell({ text }) {
    if (text === '') return null;
    return <ReactMarkdown components={CELL_COMPONENTS}>{text}</ReactMarkdown>;
}

function MarkdownTable({ head, align, rows }) {
    return (
        <table>
            <thead>
                <tr>
                    {head.map((cell, i) => (
                        <th key={`h-${i}`} style={align[i] ? { textAlign: align[i] } : undefined}>
                            <Cell text={cell} />
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, r) => (
                    <tr key={`r-${r}`}>
                        {row.map((cell, c) => (
                            <td
                                key={`c-${r}-${c}`}
                                data-label={head[c] ?? ''}
                                style={align[c] ? { textAlign: align[c] } : undefined}
                            >
                                <Cell text={cell} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function MarkdownTextImpl({ text }) {
    const blocks = useMemo(() => splitTableBlocks(text), [text]);

    return (
        <>
            {blocks.map((block, i) =>
                block.type === 'table' ? (
                    <MarkdownTable
                        key={`t-${i}`}
                        head={block.head}
                        align={block.align}
                        rows={block.rows}
                    />
                ) : (
                    <ReactMarkdown key={`p-${i}`} components={BLOCK_COMPONENTS}>
                        {block.value}
                    </ReactMarkdown>
                )
            )}
        </>
    );
}

export default React.memo(MarkdownTextImpl);